import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Home, FileText, Shield, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import ShareButtons from '@/components/ShareButtons';
import SEO, { SEOConfigs } from '@/components/SEO';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AboutPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/api/about-content`);
        if (response.data.content) {
          setContent(response.data.content);
        }
      } catch (error) {
        console.error('Error loading about content:', error);
        setContent(getDefaultContent());
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const getDefaultContent = () => {
    return `<h1>About Debatefy</h1>
    <p>
      <strong>Debatefy</strong> is an innovative AI-moderated debate platform that 
      promotes fact-based and solution-oriented discussions between two participants.
    </p>
    
    <h2>Our Vision</h2>
    <p>
      We believe in the power of constructive dialogue. Debatefy creates a space 
      where different perspectives can meet without focusing on rhetorical victories. 
      Instead, we promote insight, understanding, and mutual growth.
    </p>

    <h2>What Sets Us Apart</h2>
    <ul>
      <li><strong>AI Moderation:</strong> Neutral oversight by advanced AI models</li>
      <li><strong>Fact-Checking:</strong> Real-time verification by Perplexity AI</li>
      <li><strong>Insight Scoring:</strong> Rewarding depth over rhetoric</li>
      <li><strong>Split-Timer:</strong> Time to think without pressure</li>
      <li><strong>Multilingual:</strong> Support for German, English, Spanish, French, and Italian</li>
    </ul>

    <h2>Contact & Legal</h2>
    <p>
      For legal information and imprint, please see our Terms and Privacy Policy pages.
    </p>
    
    <p class="text-sm text-muted-foreground mt-8">
      © 2025 Debatefy.online - Where minds meet to grow
    </p>`;
  };

  return (
    <>
      <SEO {...SEOConfigs.about} />
      <div className="feature-detail-page">
        <div className="feature-detail-container">
          <div className="feature-detail-header">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="home-button"
            >
              <Home className="mr-2 h-4 w-4" />
              Home
            </Button>
            <ShareButtons 
              url="https://debatefy.online/about"
              title="About Debatefy - AI-Powered Debate Platform"
              description="Learn about Debatefy's mission to promote fair, fact-based debates with AI support."
            />
          </div>

          <Card className="feature-detail-card">
            <CardContent className="feature-detail-content">
              <div className="content-text">
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                )}
              </div>
              
              {/* Legal Links */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Legal</h3>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/privacy-policy" 
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <Shield className="h-4 w-4" />
                    Privacy Policy
                  </Link>
                  <Link 
                    to="/terms-of-service" 
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    Terms of Service
                  </Link>
                  <Link 
                    to="/imprint" 
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <Info className="h-4 w-4" />
                    Imprint
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AboutPage;
